import * as Sentry from "@sentry/react";
import { scrubBreadcrumb, scrubEvent } from "@race/observability";

// The scrubbing core is shared with the backend and video-processor — see
// `packages/observability`. Re-exported here so existing imports from
// `@/observability/sentry` keep working.
export {
  scrubBreadcrumb,
  scrubEvent,
  stripQuery,
  redactUrlSecrets,
} from "@race/observability";

let initialized = false;

export interface CaptureContext {
  tags?: Record<string, string | number | boolean>;
  user?: { id: string };
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

/** Test-only: resets the initialized flag between tests. */
export function resetSentryForTesting(): void {
  initialized = false;
}

/** Initialize Sentry. No-op when `VITE_SENTRY_DSN` is unset. */
export function initSentry(opts: { service: string }): void {
  // The deploy-time prerender (scripts/prerender.mjs) runs the production
  // bundle on 127.0.0.1 where every API call fails CORS — those "Network
  // Error" events are deploy noise, not user errors, so never initialize.
  if (window.__PRERENDER__) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const release = import.meta.env.VITE_SENTRY_RELEASE || undefined;

  Sentry.init({
    dsn,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    ...(release ? { release } : {}),
    sampleRate: 1.0,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubEvent,
  });
  Sentry.setTag("service", opts.service);
  initialized = true;
}

export function captureError(error: unknown, context?: CaptureContext): void {
  if (!initialized) return;
  Sentry.withScope((scope) => {
    if (context?.tags) {
      for (const [key, value] of Object.entries(context.tags)) {
        scope.setTag(key, value);
      }
    }
    if (context?.extra) {
      for (const [key, value] of Object.entries(context.extra)) {
        scope.setExtra(key, value);
      }
    }
    if (context?.user) {
      scope.setUser(context.user);
    }
    if (context?.fingerprint) {
      scope.setFingerprint(context.fingerprint);
    }
    Sentry.captureException(error);
  });
}

/**
 * React 18+ `onRecoverableError` hook. When React discards a failed render
 * and retries from scratch (error-boundary recovery, hydration mismatch),
 * the original error never surfaces elsewhere — capturing it here prevents
 * downstream symptoms (e.g. router invariants during the recovery re-render)
 * from masking the true cause.
 */
export function reportRecoverableError(
  error: unknown,
  errorInfo: { componentStack?: string },
): void {
  captureError(error, {
    tags: { recoverable_error: true },
    extra: { component_stack: errorInfo.componentStack },
  });
}

interface ApiErrorShape {
  config?: { method?: string; url?: string };
  response?: { status?: number };
  code?: string;
}

/**
 * Mirror of the backend `shouldCaptureError` policy: capture 5xx and
 * network-level failures; skip 4xx (operational, handled in the UI) and
 * client-initiated cancellations. Note: axios uses ECONNABORTED for
 * timeouts (a hanging backend), so those are captured, not skipped.
 */
export function shouldCaptureApiError(error: ApiErrorShape): boolean {
  if (error.code === "ERR_CANCELED") {
    return false;
  }
  if (!error.response) return true;
  return error.response.status !== undefined && error.response.status >= 500;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Normalize dynamic path segments so `/sessions/123` and `/sessions/456`
 * group into one Sentry issue.
 */
export function normalizeApiPath(url: string): string {
  const path = url.split("?")[0];
  return path
    .split("/")
    .map((segment) => {
      if (/^\d+$/.test(segment)) return ":id";
      if (UUID_RE.test(segment)) return ":uuid";
      return segment;
    })
    .join("/");
}

/** Capture Axios errors that indicate real failures (5xx / network). */
export function captureApiError(error: unknown): void {
  if (!initialized) return;
  const err = error as ApiErrorShape;
  if (!shouldCaptureApiError(err)) return;

  const method = (err.config?.method || "unknown").toLowerCase();
  const status = err.response?.status;
  const url = err.config?.url || "unknown";
  const normalizedPath = normalizeApiPath(url);
  const statusTag = status === undefined ? "network" : String(status);

  captureError(error, {
    tags: {
      api_method: method,
      api_status: statusTag,
      api_path: normalizedPath,
    },
    // Query-stripped: query strings may carry tokens or other sensitive values.
    extra: { url: url.split("?")[0] },
    fingerprint: ["api", method, statusTag, normalizedPath],
  });
}
